import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { logger } from '../../../utils/logger';

const UserManagementPanel = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    loadUsers();
  }, [filterType]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      let query = supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false })?.limit(10);

      if (filterType !== 'all') {
        query = query?.eq('user_type', filterType);
      }

      const { data, error } = await query;

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      logger?.error('Failed to load users', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users?.filter(user => {
    if (!searchQuery) return true;
    let query = searchQuery?.toLowerCase();
    return (
      user?.first_name?.toLowerCase()?.includes(query) ||
      user?.last_name?.toLowerCase()?.includes(query) ||
      user?.email?.toLowerCase()?.includes(query)
    );
  });

  return (
    <div className="bg-card rounded-xl border border-border p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-heading font-semibold text-foreground flex items-center gap-2">
          <Icon name="Users" size={20} strokeWidth={2} />
          User Management
        </h2>
        <button
          onClick={() => navigate('/user-engagement-analytics')}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
        >
          View Analytics
          <Icon name="ChevronRight" size={16} strokeWidth={2} />
        </button>
      </div>
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Icon name="Search" size={18} strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e?.target?.value)}
          className="px-4 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Users</option>
          <option value="patient">Patients</option>
          <option value="caregiver">Caregivers</option>
        </select>
      </div>
      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredUsers?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Users" size={48} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No users found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider py-3 px-4">User</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider py-3 px-4">Type</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider py-3 px-4">Status</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider py-3 px-4">Joined</th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredUsers?.map((user) => (
                <tr key={user?.id} className="hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {user?.first_name} {user?.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      user?.user_type === 'caregiver' ?'bg-purple-50 text-purple-700 border border-purple-200' :'bg-blue-50 text-blue-700 border border-blue-200'
                    }`}>
                      {user?.user_type === 'caregiver' ? 'Caregiver' : 'Patient'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      user?.subscription_status === 'active' ?'bg-green-50 text-green-700 border border-green-200' :'bg-gray-50 text-gray-700 border border-gray-200'
                    }`}>
                      {user?.subscription_status === 'active' ? 'Premium' : 'Free'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-sm text-muted-foreground">
                      {new Date(user?.created_at)?.toLocaleDateString()}
                    </p>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      onClick={() => navigate(`/user-engagement-analytics?userId=${user?.id}`)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserManagementPanel;